// ResourceList.js - Updated with new UI requirements
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  CircularProgress,
  Divider,
  Button,
  Collapse,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import DeskIcon from '@mui/icons-material/Desk';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import { API_BASE_URL } from 'config/api';

// Helper to get image URL, handling both absolute and relative paths
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  return imagePath.startsWith('http') ? imagePath : `${API_BASE_URL}${imagePath}`;
};

const ResourceListItem = styled(ListItem)(({ theme, selected }) => ({
  padding: theme.spacing(1.5),
  borderLeft: '3px solid',
  borderLeftColor: selected ? theme.palette.primary.main : 'transparent',
  backgroundColor: selected ? theme.palette.primary.light + '10' : theme.palette.background.paper,
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'flex-start',
  '&:hover': {
    backgroundColor: selected ? theme.palette.primary.light + '20' : theme.palette.action.hover,
    borderLeftColor: selected ? theme.palette.primary.main : theme.palette.primary.light,
  }
}));

const InfoIconStyled = styled(InfoIcon)(({ theme }) => ({
  color: theme.palette.grey[400],
  fontSize: '20px',
  position: 'absolute',
  bottom: '5px',
  left: '5px',
  transition: 'color 0.2s ease',
  '.MuiListItem-root:hover &': {
    color: theme.palette.grey[700],
  }
}));

const ResourceList = ({
  bookingType,
  loadingResources,
  resources,
  selectedResource,
  handleResourceSelect,
  onViewDetails = () => {},
  autoSelectSingle = false
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const prevBookingTypeRef = useRef(bookingType);
  const prevSelectedResourceRef = useRef(null);
  
  useEffect(() => {
    if (bookingType !== prevBookingTypeRef.current) {
      setIsCollapsed(false);
      prevBookingTypeRef.current = bookingType;
    }
  }, [bookingType]);
  
  useEffect(() => {
    if (autoSelectSingle && resources && resources.length > 0) {
      handleResourceSelect(resources[0]);
      setIsCollapsed(true);
    }
  }, [autoSelectSingle, resources, handleResourceSelect]);

  useEffect(() => {
    if (selectedResource && 
        (!prevSelectedResourceRef.current || 
         getResourceKey(selectedResource) !== getResourceKey(prevSelectedResourceRef.current))) {
      
      const isInCurrentResources = resources && resources.some(r => 
        getResourceKey(r) === getResourceKey(selectedResource)
      );
      
      if (isInCurrentResources) {
        setIsCollapsed(true);
      }
    }
    
    prevSelectedResourceRef.current = selectedResource;
  }, [selectedResource, resources]);
  
  const handleSelect = (resource, e) => {
    e.stopPropagation();
    handleResourceSelect(resource);
    setIsCollapsed(true);
  };
  
  const handleViewDetails = (resource) => {
    onViewDetails(resource);
  };
  
  const getResourceKey = (resource) => {
    if (!resource) return null;
    return bookingType === 'MR' 
      ? (resource.id || resource.slug) 
      : (resource.workAreaId || resource.workAreaSlug || resource.slug);
  };
  
  const isResourceSelected = (resource) => {
    if (!selectedResource) return false;
    
    const resourceKey = getResourceKey(resource);
    const selectedKey = getResourceKey(selectedResource);
    
    return resourceKey === selectedKey;
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <Paper elevation={0} variant="outlined" sx={{ px: 1.5, py: 0, mb: 1, borderRadius: 1 }}>      
      {/* Collapsed view with Change button */}
      {isCollapsed && selectedResource && (
        <Box sx={{ py: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                backgroundImage: bookingType === 'MR'
                  ? (selectedResource.image ? `url(${getImageUrl(selectedResource.image)})` : 'none')
                  : (selectedResource.images && selectedResource.images.length > 0 ? `url(${getImageUrl(selectedResource.images[0].url)})` : 'none'),
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundColor: 'grey.200',
                flexShrink: 0,
                mr: 1.5
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0 }}>
                {bookingType === 'MR' 
                  ? selectedResource.name 
                  : (selectedResource.workAreaName || selectedResource.name)}
              </Typography>
              {bookingType === 'MR' ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0 }}>
                  {`${selectedResource.capacity || 1} ${selectedResource.capacity === 1 ? 'person' : 'people'} • £${selectedResource.hourly_rate || 15}/hour`}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0 }}>
                  {`${selectedResource.floorName || ''} • ${selectedResource.hotDesksCount || selectedResource.capacity || 1} desk${(selectedResource.hotDesksCount || selectedResource.capacity || 1) !== 1 ? 's' : ''}`}
                </Typography>
              )}
            </Box>
          </Box>
          
          {/* Change button - only show if there's more than one resource */}
          {resources && resources.length > 1 && (
            <Button 
              variant="outlined" 
              size="small"
              sx={{ minWidth: '60px', py: 0.5 }}
              onClick={() => setIsCollapsed(false)}
            >
              Change
            </Button>
          )}
        </Box>
      )}
      
      {/* Expanded list view with collapse button */}
      <Collapse in={!isCollapsed}>
        {/* When there's a selected resource, show title and collapse button */}
        {selectedResource && !isCollapsed && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5, mb: 1 }}>
            
            <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{ pl: 1 }}>
              Select your space
            </Typography>
            <IconButton 
              onClick={toggleCollapse} 
              size="small"
              color="primary"
              sx={{ p: 0.5 }}
            >
              <ExpandLessIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
        
        {loadingResources ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : resources && resources.length > 0 ? (
          <List sx={{ width: '100%', p: 0 }}>
            {resources.map((resource, index) => {
              const selected = isResourceSelected(resource);
              const key = getResourceKey(resource);
              
              return (
                <React.Fragment key={key}>
                  {index > 0 && <Divider />}
                  <ResourceListItem 
                    selected={selected}
                    alignItems="flex-start"
                    onClick={() => handleViewDetails(resource)}
                  >
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 1,
                        backgroundImage: bookingType === 'MR'
                          ? (resource.image ? `url(${getImageUrl(resource.image)})` : 'none')
                          : (resource.images && resource.images.length > 0 ? `url(${getImageUrl(resource.images[0].url)})` : 'none'),
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundColor: 'grey.200',
                        flexShrink: 0,
                        mr: 1.5,
                        position: 'relative'
                      }}
                    >
                      <InfoIconStyled />
                    </Box>
                    
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight="bold">
                          {bookingType === 'MR' 
                            ? resource.name 
                            : (resource.workAreaName || resource.name)
                          }
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {bookingType === 'MR' ? (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              <Chip
                                icon={<PeopleIcon fontSize="small" />}
                                label={`${resource.capacity || 1} ${resource.capacity === 1 ? 'person' : 'people'}`}
                                size="small"
                              />
                              <Chip
                                icon={<AccessTimeIcon fontSize="small" />}
                                label={`£${resource.hourly_rate || 15}/hour`}
                                size="small"
                              />
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">
                                {resource.floorName || ''}
                              </Typography>
                              <Chip
                                icon={<DeskIcon fontSize="small" />}
                                label={`${resource.hotDesksCount || resource.capacity || 1} desk${(resource.hotDesksCount || resource.capacity || 1) !== 1 ? 's' : ''}`}
                                size="small"
                                sx={{ ml: 0.5 }} 
                              />
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    
                    <ListItemSecondaryAction>
                      {!selected && (
                        <Button 
                          variant="contained" 
                          size="small" 
                          color="primary"
                          sx={{ py: 0, px: 1, minWidth: '60px' }}
                          onClick={(e) => handleSelect(resource, e)}
                        >
                          SELECT
                        </Button>
                      )}
                    </ListItemSecondaryAction>
                  </ResourceListItem>
                </React.Fragment>
              );
            })}
          </List>
        ) : (
          <Box sx={{ py: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              No {bookingType === 'MR' ? 'meeting rooms' : 'hot desk areas'} found matching your filters.
            </Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default ResourceList;